import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  IconButton,
  Stack,
  Button,
  Alert
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import ActionButton from '@components/common/ActionButton';
import type { ChecklistItem, ChecklistFormData } from '../types';
import { validateChecklistItem } from '../utils';

type SideEditPanelProps = {
  mode: 'add' | 'edit';
  item?: ChecklistItem;
  position?: number;
  onSubmit: (data: ChecklistFormData) => void;
  onCancel: () => void;
};

export const SideEditPanel: React.FC<SideEditPanelProps> = ({
  mode,
  item,
  position,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState<ChecklistFormData>({
    seq: '',
    text: ''
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (mode === 'edit' && item) {
      setFormData({
        seq: item.seq,
        text: item.text
      });
    } else if (mode === 'add' && position !== undefined) {
      setFormData({
        seq: String(position + 1),
        text: ''
      });
    }
    setErrors([]);
    setTouched({});
  }, [mode, item, position]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateChecklistItem(formData);
    setErrors(validationErrors);
    setTouched({ seq: true, text: true });

    if (validationErrors.length === 0 && formData.text.trim()) {
      onSubmit(formData);
    }
  };

  const handleInputChange =
    (field: keyof ChecklistFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setFormData(prev => ({ ...prev, [field]: value }));

      if (touched[field]) {
        const newFormData = { ...formData, [field]: value };
        const validationErrors = validateChecklistItem(newFormData);
        setErrors(validationErrors);
      }
    };

  const handleBlur = (field: keyof ChecklistFormData) => () => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const validationErrors = validateChecklistItem(formData);
    setErrors(validationErrors);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit(e as any);
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        p: 3,
        backgroundColor: 'background.paper'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          {mode === 'add' ? 'Add Checklist Item' : 'Edit Checklist Item'}
        </Typography>
        <IconButton onClick={onCancel} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
      >
        <Stack spacing={3} sx={{ flex: 1 }}>
          <TextField
            label="Position"
            value={formData.seq}
            onChange={handleInputChange('seq')}
            onBlur={handleBlur('seq')}
            type="number"
            size="small"
            fullWidth
            error={touched.seq && errors.some(e => e.includes('Sequence'))}
            InputProps={{
              inputProps: { min: 1 }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.default'
              }
            }}
          />

          <TextField
            label="Checklist Item Text"
            value={formData.text}
            onChange={handleInputChange('text')}
            onBlur={handleBlur('text')}
            onKeyDown={handleKeyDown}
            multiline
            rows={4}
            fullWidth
            autoFocus
            required
            error={touched.text && errors.some(e => e.includes('Text'))}
            placeholder="Enter the checklist item description..."
            helperText="Press Ctrl/Cmd + Enter to save, Escape to cancel"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.default'
              }
            }}
          />
        </Stack>

        <Stack
          direction="row"
          spacing={2}
          sx={{ mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <Button
            variant="outlined"
            onClick={onCancel}
            fullWidth
            sx={{
              textTransform: 'none',
              borderColor: 'divider',
              color: 'text.secondary'
            }}
          >
            Cancel
          </Button>
          <ActionButton
            type="submit"
            onClick={handleSubmit}
            disabled={!formData.text.trim()}
            fullWidth
          >
            {mode === 'add' ? 'Add Item' : 'Update Item'}
          </ActionButton>
        </Stack>
      </Box>
    </Box>
  );
};
